import {
  EXCLUDED_INDICATORS,
  ORDER,
  RESTRICTED_INDICATORS,
} from "~/components/dashboard/single-result/constants";
import { getIndicatorIndices } from "~/components/dashboard/single-result/indices";

export interface IndicatorResult {
  id: string;
  value: number;
  createdAt: string;
  indicator: {
    id: string;
    minValue: number;
    maxValue: number;
    model: {
      id: string;
      name: string;
    };
    indicatorDefinition: {
      id: string;
      name: string;
      unit: string;
      description?: string | null;
      displayName?: string | null;
      displayDecimalPlaces?: number | null;
    };
  };
}

export interface TestWithResults {
  id: string;
  name: string;
  originalModelName?: string | null;
  results: IndicatorResult[];
}

export interface NormalizedIndicator {
  indicatorName: string;
  displayName: string;
  unit: string;
  minValue: number;
  maxValue: number;
}

export interface ComparisonDataPoint {
  indicator: string;
  displayName: string;
  unit: string;
  [testId: string]: string | number;
}

export const COLOR_PALETTE = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#9333ea",
  "#0ea5e9",
  "#ef4444",
  "#22c55e",
  "#8b5cf6",
  "#facc15",
  "#14b8a6",
  "#d946ef",
  "#fb7185",
];

export const normalizeIndicatorValue = (
  value: number,
  min: number,
  max: number
): number => {
  if (max === min) {
    return 50;
  }
  const normalized = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, normalized));
};

/**
 * Calculate the index rating (1-5) for a given indicator value
 */
export const calculateIndexRating = (
  value: number,
  minValue: number,
  maxValue: number,
  indicatorName: string
): number => {
  const config = getIndicatorIndices(indicatorName, minValue, maxValue);

  // Normalize value to 0-1 scale based on the indicator's range
  const range = maxValue - minValue;
  const normalizedValue = range === 0 ? 0.5 : (value - minValue) / range;

  // Find which breakpoint bucket the value falls into
  const breakpoints = config.breakpoints;
  for (let i = 0; i < breakpoints.length; i++) {
    const bp = breakpoints[i];
    if (normalizedValue >= bp.start && normalizedValue <= bp.end) {
      // Return index + 1 to get 1-based rating (1-5)
      return i + 1;
    }
  }

  // Default to middle rating if not found
  return Math.ceil(breakpoints.length / 2);
};

export const filterAndSortResults = (
  results: IndicatorResult[] | undefined | null,
  originalModelName?: string | null
): IndicatorResult[] => {
  if (!results || results.length === 0) {
    return [];
  }

  const modelName = originalModelName ?? results[0]?.indicator?.model?.name;

  return results
    .filter((result) => {
      const name = result.indicator.indicatorDefinition.name;
      if (RESTRICTED_INDICATORS.includes(name.toLowerCase())) {
        return false;
      }
      return !EXCLUDED_INDICATORS.includes(name.toLowerCase());
    })
    .filter((result) => result.indicator.model.name === modelName)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .filter(
      (result, index, self) =>
        self.findIndex(
          (r) =>
            r.indicator.indicatorDefinition.name ===
            result.indicator.indicatorDefinition.name
        ) === index
    )
    .sort(
      (a, b) =>
        ORDER.indexOf(a.indicator.indicatorDefinition.name.toLowerCase()) -
        ORDER.indexOf(b.indicator.indicatorDefinition.name.toLowerCase())
    );
};

export interface IndexComparisonDataPoint {
  indicator: string;
  displayName: string;
  unit: string;
  [testId: string]: string | number;
}

/**
 * Build comparison data using index ratings (1-5 scale)
 */
export const buildIndexComparisonData = (
  tests: TestWithResults[]
): IndexComparisonDataPoint[] => {
  const indicatorMap = new Map<string, IndexComparisonDataPoint>();

  tests.forEach((test) => {
    const filteredResults = filterAndSortResults(
      test.results,
      test.originalModelName
    );

    filteredResults.forEach((result) => {
      const indicatorName = result.indicator.indicatorDefinition.name;
      const displayName =
        result.indicator.indicatorDefinition.displayName || indicatorName;
      const unit = result.indicator.indicatorDefinition.unit || "";

      if (!indicatorMap.has(indicatorName)) {
        indicatorMap.set(indicatorName, {
          indicator: indicatorName,
          displayName,
          unit,
        });
      }

      const dataPoint = indicatorMap.get(indicatorName)!;
      const indexRating = calculateIndexRating(
        result.value,
        result.indicator.minValue,
        result.indicator.maxValue,
        indicatorName
      );

      dataPoint[test.id] = indexRating;
      dataPoint[`${test.id}_raw`] = result.value;
      dataPoint[`${test.id}_min`] = result.indicator.minValue;
      dataPoint[`${test.id}_max`] = result.indicator.maxValue;
    });
  });

  const orderedIndicators = [...indicatorMap.values()].sort((a, b) => {
    const aIndex = ORDER.indexOf(a.indicator.toLowerCase());
    const bIndex = ORDER.indexOf(b.indicator.toLowerCase());
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });

  return orderedIndicators;
};

/**
 * Build comparison data with actual values (for per-indicator charts)
 */
export const buildActualValuesData = (
  tests: TestWithResults[]
): ComparisonDataPoint[] => {
  const indicatorMap = new Map<string, ComparisonDataPoint>();

  tests.forEach((test) => {
    const filteredResults = filterAndSortResults(
      test.results,
      test.originalModelName
    );

    filteredResults.forEach((result) => {
      const indicatorName = result.indicator.indicatorDefinition.name;
      const displayName =
        result.indicator.indicatorDefinition.displayName || indicatorName;
      const unit = result.indicator.indicatorDefinition.unit || "";

      if (!indicatorMap.has(indicatorName)) {
        indicatorMap.set(indicatorName, {
          indicator: indicatorName,
          displayName,
          unit,
          minValue: result.indicator.minValue,
          maxValue: result.indicator.maxValue,
        });
      }

      const dataPoint = indicatorMap.get(indicatorName)!;
      dataPoint[test.id] = result.value;
    });
  });

  const orderedIndicators = [...indicatorMap.values()].sort((a, b) => {
    const aIndex = ORDER.indexOf(a.indicator.toLowerCase());
    const bIndex = ORDER.indexOf(b.indicator.toLowerCase());
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });

  return orderedIndicators;
};

// Keep for backwards compatibility but prefer buildIndexComparisonData or buildActualValuesData
export const buildComparisonData = buildIndexComparisonData;

export const getTestColor = (index: number): string => {
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

/**
 * Encodes an array of test IDs into a URL-safe base64 string.
 */
export const encodeTestIds = (ids: string[]): string => {
  const json = JSON.stringify(ids);
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * Decodes a URL-safe base64 string back into an array of test IDs.
 * Falls back to comma-separated parsing for backwards compatibility.
 */
export const decodeTestIds = (encoded: string): string[] => {
  if (!encoded) {
    return [];
  }

  // Try to decode as base64 first
  try {
    const padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const paddedWithEquals = padded + "=".repeat((4 - (padded.length % 4)) % 4);
    const decoded = atob(paddedWithEquals);
    const parsed = JSON.parse(decoded);
    if (Array.isArray(parsed)) {
      return parsed.filter((id): id is string => typeof id === "string");
    }
  } catch {
    // Not base64, fall through to comma-separated parsing
  }

  // Fallback: parse as comma-separated IDs (backwards compatibility)
  return encoded.split(",").filter(Boolean);
};
